import type { OrganizationUnitType } from '../models/organization.model';
/*
  The other "what may this type hold" rules. They keep their own files so
  that the employment, position and station questions can be read — and
  argued about — without the tree; `checkOeTypeChange` is the one place that
  needs all of them.
*/
import { canHoldEmployment } from './oe-employment';
import { canHoldPosition } from './oe-position';
import { canHoldStation } from './oe-station';

/**
 * Where an organization unit is allowed to sit in the tree.
 *
 * One rule, asked by two callers. The server decides — it is the only place a
 * request cannot walk around — and the client asks the same function so the
 * type is never offered where saving it would fail.
 *
 * Until 2026-10-21 this was written the other way round: a handful of rules
 * about the one type that was special, the company. The customer then gave
 * the shape they want the tree to have, and several types with different
 * answers do not fit into "everything is allowed except…" any more. So the
 * rule is now the shape itself, one line per parent:
 *
 *     Wurzel       → Firma (genau eine)
 *     Firma        → Firma, Profitcenter
 *     Profitcenter → Abteilung
 *     Abteilung    → Abteilung
 *
 * The station had a line of its own ("Station → nichts") until 2026-10-27,
 * when it left the tree for a table of its own: it is held by a unit the way
 * a Stelle is, see `canHoldStation`.
 *
 * Read it downwards and it is the customer's sentence. Read one line and it
 * answers "may this go here?" without the reader holding the others in
 * their head — which is what the old form asked of them, and why the hole
 * company → department → department → company had to be argued about rather
 * than looked up.
 *
 * Two things stay outside the table, because they are not about types:
 * a position is never a parent of a company, and the root takes one child
 * however many its line would allow.
 */

export type OeHierarchyViolation =
  /** The table has no such line: this type may not sit under that parent. */
  | 'TYPE_NOT_ALLOWED_UNDER_PARENT'
  /**
   * The parent is a leaf. No type is one since the station left the tree,
   * but the table may grow one again, and the code is what the client and
   * the server already agree on for it.
   */
  | 'PARENT_TAKES_NO_CHILDREN'
  | 'ROOT_TAKES_ONE'
  /** A type change the units below would no longer fit under. */
  | 'CHILDREN_NOT_ALLOWED'
  /** A type change away from one that may employ, with contracts still on it. */
  | 'COMPANY_EMPLOYS'
  /** A type change to one that holds no Stellen, with Stellen still on it. */
  | 'POSITIONS_NOT_ALLOWED'
  /** A type change to one that holds no stations, with stations still on it. */
  | 'STATIONS_NOT_ALLOWED';

export interface OeParent {
  /** Type of the unit this one hangs under. Null only for the root. */
  type: OrganizationUnitType | null;
  /**
   * Whether this parent already holds a child OE besides the one being placed.
   *
   * Only the root reads it, and only to refuse a second child. Phrased as
   * "other" so the same field works when a unit is being moved to a parent it
   * already sits under.
   */
  hasOtherChild: boolean;
}

/**
 * The tree, as the customer described it on 2026-10-21.
 *
 * Every type is a key: an empty list would be a statement ("this holds
 * nothing"), while a missing key would be an oversight, and the two should
 * not look the same.
 */
const ALLOWED_CHILDREN: Readonly<
  Record<OrganizationUnitType, readonly OrganizationUnitType[]>
> = {
  organization: ['company'],
  company: ['company', 'profit_center'],
  profit_center: ['department'],
  department: ['department'],
};

/** Whether units may be created beneath this type at all. */
export function canHoldChildOes(type: OrganizationUnitType): boolean {
  return ALLOWED_CHILDREN[type].length > 0;
}

/** The child types this parent's line allows, whatever else may forbid them. */
export function childTypesOf(
  type: OrganizationUnitType,
): readonly OrganizationUnitType[] {
  return ALLOWED_CHILDREN[type];
}

/**
 * Why this unit may not hang there, or null when it may.
 *
 * A parent of `null` is the root being seeded; nothing sits above it, so
 * nothing can be broken.
 */
export function checkOeParent(
  type: OrganizationUnitType,
  parent: OeParent,
): OeHierarchyViolation | null {
  if (parent.type === null) return null;

  if (!canHoldChildOes(parent.type)) return 'PARENT_TAKES_NO_CHILDREN';

  return ALLOWED_CHILDREN[parent.type].includes(type)
    ? null
    : 'TYPE_NOT_ALLOWED_UNDER_PARENT';
}

/**
 * Why this parent has no room for another child, or null when it has.
 *
 * Only the root is limited, and to one. Everything below it takes as many as
 * its line allows — the customer's words were that the next level may have
 * several.
 */
export function checkOeSlot(parent: OeParent): OeHierarchyViolation | null {
  return parent.type === 'organization' && parent.hasOtherChild
    ? 'ROOT_TAKES_ONE'
    : null;
}

/** What a unit is carrying, for the question below. */
export interface OeContents {
  /** Types of the units directly beneath it. */
  childTypes: readonly OrganizationUnitType[];
  hasEmployments: boolean;
  hasPositions: boolean;
  hasStations: boolean;
}

/**
 * Why this unit may not become that type, or null when it may.
 *
 * Moving a unit is guarded by `checkOeParent`; changing its type is the quiet
 * way round that, because nothing moves. Four things can be left stranded:
 *
 *  - **The units below.** Demote the company above a row of companies and
 *    every one of them is suddenly somewhere it could never have been placed.
 *  - **The contracts.** A contract belongs to the legal entity that employs
 *    the person; a department is not one, and neither is a profit centre.
 *    That route stayed open longer than the first, because creating a
 *    contract is guarded by the company scope while editing and deleting one
 *    are not.
 *  - **The Stellen and the stations.** The root holds neither, so a unit
 *    carrying them cannot become it.
 *
 * Each is checked against the same tables the placement uses, so a type added
 * to `ALLOWED_CHILDREN` is covered here without a second edit.
 */
export function checkOeTypeChange(
  newType: OrganizationUnitType,
  unit: OeContents,
): OeHierarchyViolation | null {
  const allowed = ALLOWED_CHILDREN[newType];
  if (unit.childTypes.some((child) => !allowed.includes(child))) {
    return 'CHILDREN_NOT_ALLOWED';
  }
  if (unit.hasEmployments && !canHoldEmployment(newType))
    return 'COMPANY_EMPLOYS';
  if (unit.hasPositions && !canHoldPosition(newType))
    return 'POSITIONS_NOT_ALLOWED';
  if (unit.hasStations && !canHoldStation(newType))
    return 'STATIONS_NOT_ALLOWED';
  return null;
}

/**
 * The types a newly created unit may take under this parent.
 *
 * Derived from the table rather than listed again, so the choice offered and
 * the choice accepted cannot drift apart. `organization` never appears: the
 * root is seeded, never created.
 */
export function allowedChildOeTypes(parent: OeParent): OrganizationUnitType[] {
  // A full parent offers nothing, whatever the types would allow.
  if (checkOeSlot(parent)) return [];
  if (parent.type === null) return [];
  return ALLOWED_CHILDREN[parent.type].filter(
    (type) => checkOeParent(type, parent) === null,
  );
}
